import type { ClickstreamPayload } from '@zg-rentals/clickstream';

export interface TrackEvent {
  (clickstreamEvent: ClickstreamPayload): void;
}

export interface EmitMetric {
  (metricName: string): void;
}

const EventTypes = {
  Landlord: 'Landlord',
  Renter: 'Renter',
};

const TRIGGER_TYPE = {
  IMPRESSION: 'impression',
  INTERACTION: 'interaction',
};

const TRIGGER_LOCATION = {
  IRENE_APPLICATION_DASHBOARD: 'irene_applications_dashboard',
  RENTAL_APPLICATION: 'rental_application',
  IRENE_IDV_MODAL: 'irene_idv_modal',
};

const TRIGGER_SOURCE = {
  NEXT: 'next',
};

const TRIGGER_OBJECT = {
  NO_TRIGGER_OBJECT: 'no_trigger_object',
  RENTAL_APPLICATION_OBJECT_SCREENING_SECTION: 'rental_application_object_screening_section',
  PRINT_PRIVACY_REMINDER_MODAL: 'print_privacy_reminder_modal',
};

const SEMANTIC_EVENT = {
  CONTINUE_ID_VERIFY: 'continue_id_verify',
  PRINT_CREDIT_REPORT: 'print_credit_report',
  PRINT_BACKGROUND_REPORT: 'print_background_report',
};

function getEnvelope(eventTypeId: string, eventType: string) {
  return {
    event_type_id: eventTypeId,
    event_template_id: eventType === EventTypes.Landlord ? '168' : '71',
    event_type_version_id: '1',
    event_template_version_id: '1',
    event_client_start_dtm: new Date().toISOString(),
  };
}

function getSemantic(semanticEventName: string) {
  return {
    semantic_event_nm: semanticEventName,
    topic_tag_txt: ['rental_application'],
  };
}

interface ClickstreamEnvelopeBlock {
  event_type_id: string;
  event_template_id: string;
  event_type_version_id: string;
  event_template_version_id: string;
  event_client_start_dtm: string;
}

interface ClickstreamTriggerBlock {
  trigger_type_nm: string;
  trigger_location_nm: string;
  trigger_source_nm: string;
  trigger_object_nm: string;
}

interface ClickstreamSemanticBlock {
  semantic_event_nm: string;
  topic_tag_txt: Array<string>;
}

export interface ClickstreamEvent {
  envelope: ClickstreamEnvelopeBlock;
  clickstream_trigger: ClickstreamTriggerBlock;
  semantic: ClickstreamSemanticBlock;
}

export const RENTER_CLICKSTREAM_EVENTS = {
  // https://dataportal.zillowgroup.net/event-registry/2022
  submitIdv: {
    envelope: getEnvelope('2022', EventTypes.Renter),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
      trigger_source_nm: 'button_to_submit_idv_info',
      trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
    },
    semantic: getSemantic('submit_IDV'),
  },
  // https://dataportal.zillowgroup.net/event-registry/2023
  submitIdvWithSsn: {
    envelope: getEnvelope('2023', EventTypes.Renter),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
      trigger_source_nm: 'idv_form_ssn',
      trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
    },
    semantic: getSemantic('submit_idv_with_ssn'),
  },
  // https://dataportal.zillowgroup.net/event-registry/2024
  submitKba: {
    envelope: getEnvelope('2024', EventTypes.Renter),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
      trigger_source_nm: 'button_to_submit_kba_info',
      trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
    },
    semantic: getSemantic('submit_KBA'),
  },
  // https://dataportal.zillowgroup.net/event-registry/2834 - property_info building_info
  termAgree: {
    experianCicWrittenConsentRenter: {
      envelope: getEnvelope('2834', EventTypes.Renter),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'button_to_authorize_experian',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('authorize_experian_cic'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2019
    experianTac: {
      envelope: getEnvelope('2019', EventTypes.Renter),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'button_to_accept_experian_terms',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('accept_experian_terms'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2021
    experianPrivacyPolicy: {
      envelope: getEnvelope('2021', EventTypes.Renter),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'button_to_authorize_experian_pp',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('accept_experian_privacy_policy'),
    },
    // https://dataportal.zillowgroup.net/event-registry/6075
    cicTac: {
      envelope: {
        event_type_id: '6075',
        event_template_id: '378',
        event_type_version_id: '1',
        event_template_version_id: '1',
        event_client_start_dtm: new Date().toISOString(),
      },
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'checkbox_to_accept_cic_terms',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('accept_cic_terms'),
    },
    // https://dataportal.zillowgroup.net/event-registry/6076
    cicConfirmationRenter: {
      envelope: {
        event_type_id: '6076',
        event_template_id: '378',
        event_type_version_id: '1',
        event_template_version_id: '1',
        event_client_start_dtm: new Date().toISOString(),
      },
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'checkbox_to_obtatining_bg_report',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('accept_request_for_bg_report'),
    },
  },
  termView: {
    // https://dataportal.zillowgroup.net/event-registry/2018
    experianTac: {
      envelope: getEnvelope('2018', EventTypes.Renter),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'link_to_experian_terms',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('view_experian_terms'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2020
    experianPrivacyPolicy: {
      envelope: getEnvelope('2020', EventTypes.Renter),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.RENTAL_APPLICATION,
        trigger_source_nm: 'link_to_experian_pp',
        trigger_object_nm: TRIGGER_OBJECT.RENTAL_APPLICATION_OBJECT_SCREENING_SECTION,
      },
      semantic: getSemantic('view_experian_privacy_policy'),
    },
  },
};

export const LANDLORD_CLICKSTREAM_EVENTS = {
  // https://dataportal.zillowgroup.net/event-registry/2626 - zrm_listing_info zrm_user_info
  startIdVerify: {
    envelope: getEnvelope('2626', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
      trigger_source_nm: 'begin_id_verification',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemantic('start_id_verify'),
  },
  // https://dataportal.zillowgroup.net/event-registry/6494 - zrm_listing_info zrm_user_info
  displayTerms: {
    envelope: getEnvelope('6494', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
      trigger_source_nm: 'idv_display_terms',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemantic('view_content'),
  },
  // https://dataportal.zillowgroup.net/event-registry/2632 - zrm_listing_info zrm_user_info
  completedAgreeingToAll: {
    // clicking next after agreeing to all terms
    envelope: getEnvelope('2632', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
      trigger_source_nm: 'I_agree',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemantic('continue_id_verify'),
  },
  // https://dataportal.zillowgroup.net/event-registry/2629 - zrm_listing_info zrm_user_info
  termAgree: {
    experianTac: {
      envelope: getEnvelope('2629', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'checkbox_experian_terms',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2631 - zrm_listing_info zrm_user_info
    experianPrivacyPolicy: {
      envelope: getEnvelope('2631', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'checkbox_experian_privacy_policy',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
    // https://dataportal.zillowgroup.net/event-registry/1227 - zrm_listing_info zrm_user_info
    cicTac: {
      envelope: getEnvelope('1227', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'checkbox_checkr_terms',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2627 - zrm_listing_info zrm_user_info
    experianWrittenConsentLandlord: {
      envelope: getEnvelope('2627', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'checkbox_landlord_consent',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
  },
  // https://dataportal.zillowgroup.net/event-registry/2628 - zrm_listing_info zrm_user_info
  termView: {
    experianTac: {
      envelope: getEnvelope('2628', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'clicks_experian_terms_links',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
    // https://dataportal.zillowgroup.net/event-registry/2630 - zrm_listing_info zrm_user_info
    experianPrivacyPolicy: {
      envelope: getEnvelope('2630', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'clicks_experian_privacy_policy_link',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
    // https://dataportal.zillowgroup.net/event-registry/1226 - zrm_listing_info zrm_user_info
    cicTac: {
      envelope: getEnvelope('1226', EventTypes.Landlord),
      clickstream_trigger: {
        trigger_type_nm: TRIGGER_TYPE.INTERACTION,
        trigger_location_nm: TRIGGER_LOCATION.IRENE_APPLICATION_DASHBOARD,
        trigger_source_nm: 'clicks_checkr_terms_links',
        trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
      },
      semantic: getSemantic('continue_id_verify'),
    },
  },
  // https://dataportal.zillowgroup.net/event-registry/1228 - zrm_listing_info zrm_user_info
  clickNextSubmitIDV: {
    envelope: getEnvelope('1228', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.IRENE_IDV_MODAL,
      trigger_source_nm: TRIGGER_SOURCE.NEXT,
      trigger_object_nm: 'personal_info',
    },
    semantic: getSemantic(SEMANTIC_EVENT.CONTINUE_ID_VERIFY),
  },
  // https://dataportal.zillowgroup.net/event-registry/1229 - zrm_listing_info zrm_user_info
  clickNextOntoKBA: {
    envelope: getEnvelope('1229', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'irene_idv_modal',
      trigger_source_nm: 'next',
      trigger_object_nm: 'continue_to_kba',
    },
    semantic: getSemantic('continue_id_verify'),
  },
  // https://dataportal.zillowgroup.net/event-registry/1230 - zrm_listing_info zrm_user_info
  clickNextSubmitKBA: {
    envelope: getEnvelope('1230', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'irene_idv_modal',
      trigger_source_nm: 'next',
      trigger_object_nm: 'submit_kba',
    },
    semantic: getSemantic('continue_id_verify'),
  },
  // https://dataportal.zillowgroup.net/event-registry/1231 - zrm_listing_info zrm_user_info
  idvSuccess: {
    envelope: getEnvelope('1231', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: 'irene_idv_modal',
      trigger_source_nm: 'Irene_idv_modal',
      trigger_object_nm: 'complete_id_verify',
    },
    semantic: getSemantic('complete_id_verify'),
  },
  // https://dataportal.zillowgroup.net/event-registry/8729
  viewPage: (pageName: string) => ({
    envelope: getEnvelope('8729', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: pageName,
      trigger_source_nm: pageName,
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: getSemantic('view_content'),
  }),
  // https://dataportal.zillowgroup.net/event-registry/8723
  clickNext: (pageName: string) => ({
    envelope: getEnvelope('8723', EventTypes.Landlord),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'application_purchase_flow',
      trigger_source_nm: 'next_button',
      trigger_object_nm: pageName,
    },
    semantic: getSemantic('continue_application'),
  }),
};
